import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPin, Loader2, Calculator, Globe, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { searchPlaces, useLocation } from "../lib/location";
import { formatMoney } from "../lib/money";
import { getGeo } from "../lib/geo";
import CountryOnboardingBanner from "../components/CountryOnboardingBanner";
import { ServiceOptionGrid } from "../components/ServiceOptionGrid";

const CATEGORIES = [
  "taxi", "moving", "towing", "bus",
  "crane", "luxury", "mechanic", "tire",
  "delivery", "carwash",
];

/**
 * Public "How much will it cost?" page.
 *
 * Formula, driven by the admin-editable per-country fare table:
 *   total = base_fare + (km × per_km_rate) + (minutes × per_minute_rate)
 *
 * Distance & duration come from the same OSRM road-routing used for real
 * bookings, so this page always shows the exact final total the customer
 * would pay if they placed the order.
 */
export default function Estimate() {
  const { t } = useTranslation();
  const { loc, request } = useLocation();
  const [category, setCategory] = useState("taxi");
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [rates, setRates] = useState(null);
  // Selected option_code (e.g. towing → "light" | "heavy"). Reset when
  // category changes so a stale selection isn't sent for a new category.
  const [optionCode, setOptionCode] = useState(null);
  useEffect(() => { setOptionCode(null); }, [category]);

  // Pre-fill pickup with the browser's current location.
  useEffect(() => {
    if (!pickup && loc?.lat) setPickup({ label: loc.label || `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`, lat: loc.lat, lng: loc.lng });
  }, [loc, pickup]);

  // Pull the effective country rates for the pickup point so we can show a
  // "Israel ₪5 + ₪2.5/km + ₪0.5/min" style summary panel.
  useEffect(() => {
    if (!pickup?.lat) return;
    api.get("/public/rates", { params: { lat: pickup.lat, lng: pickup.lng, category } })
      .then((r) => setRates(r.data))
      .catch(() => setRates(null));
  }, [pickup?.lat, pickup?.lng, category]);

  const compute = async () => {
    if (!pickup?.lat || !dropoff?.lat) return;
    // Crane V1 — mirror the RideRequest short-circuit: don't fire the
    // top-line /fare/estimate for crane until the customer has picked a
    // boom-height band via the ServiceOptionGrid above. Without
    // `option_code` the backend rightly returns a 422 "No active Pricing
    // V2 rule" which surfaces a scary banner on this page. The per-card
    // prices rendered by ServiceOptionGrid already give the customer the
    // right preview until they commit to a specific band.
    if (category === "crane" && !optionCode) {
      setQuote(null);
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/fare/estimate", {
        category,
        pickup_lat: pickup.lat, pickup_lng: pickup.lng,
        dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
        use_routing: true,
        // Phase-3 IL-taxi metered inputs. Ignored by every other formula.
        // In-app estimates are always booking_source="app" (adds the
        // 4.90 ILS phone/app surcharge for IL taxi under MoT tariff).
        pickup_at: new Date().toISOString(),
        booking_source: "app",
        // Selected service option (e.g. towing "light"/"heavy"). Sent
        // as option_code — backend resolver matches an active rule
        // with the same (country, category, option_code).
        option_code: optionCode || undefined,
      });
      setQuote(data);
    } catch (_) {
      setQuote(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (pickup?.lat && dropoff?.lat) compute(); },
           [pickup?.lat, dropoff?.lat, category, optionCode]);

  const currency = quote?.currency || rates?.currency || getGeo()?.currency || "usd";
  const km = quote?.distance_km ?? 0;
  const minutes = quote?.duration_min ?? 0;
  const br = quote?.breakdown || {};

  return (
    <div className="min-h-screen bg-obsidian text-white pb-32">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5" data-testid="estimate-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-black text-electric-teal">{t("estimate.eyebrow", "How much will it cost?")}</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">{t("estimate.title", "Trip price estimator")}</h1>
          <p className="text-white/60 mt-2 text-sm">{t("estimate.subtitle", "Pick your route — we'll show the exact price in your local currency before you book. No sign-in required.")}</p>
          {(() => {
            const geo = getGeo();
            if (!geo || rates) return null;
            return (
              <div
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-electric-teal/10 border border-electric-teal/30 px-2.5 py-1 text-[10px] uppercase tracking-widest font-black text-electric-teal"
                data-testid="estimate-geo-chip"
                title={t("estimate.geoTip", "Detected from your IP — auto-selects local pricing")}
              >
                <Globe size={10} /> {geo.country} · {geo.currency.toUpperCase()}
              </div>
            );
          })()}
        </div>

        {/* Coming-soon waitlist banner — only renders when the detected
            country isn't in our per-km rate table yet. */}
        <CountryOnboardingBanner source="estimate" />

        {/* Category chips */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-black border-2 transition-all ${category === c ? "bg-electric-teal text-obsidian border-electric-teal" : "bg-white/[0.03] text-white/70 border-white/10"}`}
              data-testid={`estimate-cat-${c}`}
            >
              {t(`categories.${c}`, c)}
            </button>
          ))}
        </div>

        {/* Service-option cards — data-driven from Pricing V2 rules.
            Renders large selectable cards (Taxi-style UX) for every
            category that has multiple option_codes in the DB. Hidden
            automatically when only a default rule exists.
            Country is inferred from pickup coords when available;
            falls back to detected geo, then to Israel as the tenant
            default. */}
        <ServiceOptionGrid
          country={(rates?.country || getGeo()?.country || "IL").toUpperCase()}
          category={category}
          value={optionCode}
          onChange={setOptionCode}
          pricingContext={pickup?.lat && dropoff?.lat ? {
            pickup_lat: pickup.lat, pickup_lng: pickup.lng,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            quantity: 1,
            pickup_at: new Date().toISOString(),
            booking_source: "app",
          } : undefined}
          fmtFare={(n, cur) => formatMoney(n, cur || quote?.currency || "usd")}
          testId={`estimate-service-options-${category}`}
        />

        {/* Pickup / Dropoff */}
        <div className="space-y-2">
          <PlaceInput
            testid="estimate-pickup"
            placeholder={t("estimate.pickup", "Pickup address")}
            value={pickup}
            onChange={setPickup}
            onUseMyLocation={async () => {
              const p = await request();
              if (p) setPickup({ label: p.label || `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`, lat: p.lat, lng: p.lng });
            }}
          />
          <PlaceInput
            testid="estimate-dropoff"
            placeholder={t("estimate.dropoff", "Drop-off address")}
            value={dropoff}
            onChange={setDropoff}
          />
        </div>

        {/* Country rate summary */}
        {rates && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="estimate-rate-card">
            <div className="text-[10px] uppercase font-black text-white/50 tracking-widest inline-flex items-center gap-1.5">
              <Globe size={11} /> {t("estimate.pricingIn", "Pricing in")} {rates.country_name || rates.country}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <RateChip label={t("estimate.baseFare", "Base fare")} value={formatMoney(rates.base_fare, currency)} />
              <RateChip label={t("estimate.perKm", "Per km")} value={formatMoney(rates.per_km_rate, currency)} />
              {rates.per_minute_rate ? (
                <RateChip label={t("estimate.perMinute", "Per minute")} value={formatMoney(rates.per_minute_rate, currency)} />
              ) : null}
              {rates.min_fare ? (
                <RateChip label={t("estimate.minFare", "Minimum")} value={formatMoney(rates.min_fare, currency)} />
              ) : null}
            </div>
          </div>
        )}

        {/* Total */}
        <div className="rounded-2xl border border-electric-teal/40 bg-electric-teal/5 p-5" data-testid="estimate-total-card">
          <div className="text-[10px] uppercase font-black text-electric-teal tracking-widest inline-flex items-center gap-1.5">
            <Calculator size={11} /> {t("estimate.estimatedTotal", "Estimated total")}
          </div>
          {busy ? (
            <div className="text-white/60 text-sm mt-3 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> {t("estimate.computing", "Calculating…")}</div>
          ) : !quote ? (
            <div className="text-white/50 text-sm mt-3">{t("estimate.enterBoth", "Enter both a pickup and a drop-off to see the price.")}</div>
          ) : (
            <>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <div className="text-4xl sm:text-5xl font-black text-white" data-testid="estimate-total">
                  {formatMoney(quote.estimate, currency)}
                </div>
                <div className="text-xs text-white/60">
                  {km ? `${km.toFixed(1)} km` : ""}{km && minutes ? " · " : ""}{minutes ? `${Math.round(minutes)} min` : ""}
                </div>
                {/* IL taxi tiered-tariff badge. Only rendered when the
                    engine attaches a `tariff` field (il_taxi_metered
                    formula). Silent for every other formula. */}
                {br.tariff && (
                  <span
                    data-testid="estimate-tariff-badge"
                    className={
                      "text-[10px] uppercase font-black tracking-widest px-2 py-1 rounded-full border " +
                      (br.tariff === 1
                        ? "border-electric-teal/50 text-electric-teal bg-electric-teal/10"
                        : br.tariff === 2
                        ? "border-purple-400/50 text-purple-300 bg-purple-500/10"
                        : "border-amber-400/60 text-amber-300 bg-amber-500/10")
                    }
                    title={t("estimate.tariffTip",
                      "Rate is set by the Israeli Ministry of Transport tariff active at pickup time.")}
                  >
                    {br.tariff === 1
                      ? t("estimate.tariff1", "Tariff 1 · Day")
                      : br.tariff === 2
                      ? t("estimate.tariff2", "Tariff 2 · Night")
                      : t("estimate.tariff3", "Tariff 3 · Shabbat / Holiday")}
                    {br.booking_surcharge_applied ? " · +booking" : ""}
                  </span>
                )}
              </div>
              {(br.base_fare || br.km_cost || br.minute_cost) && (
                <div className="mt-4 text-[11px] text-white/60 font-mono">
                  {formatMoney(br.base_fare || 0, currency)}
                  {" + ("}{km.toFixed(2)} × {formatMoney(br.per_km_rate || 0, currency)}
                  {br.per_minute_rate ? `) + (${Math.round(minutes)} × ${formatMoney(br.per_minute_rate, currency)})` : ")"}
                  {" = "}
                  <span className="text-white font-black">{formatMoney(quote.estimate, currency)}</span>
                </div>
              )}
              <Link
                to="/request"
                className="mt-5 inline-flex items-center gap-2 bg-electric-teal text-obsidian rounded-full px-4 py-2.5 text-xs font-black"
                data-testid="estimate-book-cta"
              >
                {t("estimate.bookNow", "Book this trip")} <ArrowRight size={12} />
              </Link>
            </>
          )}
        </div>

        <div className="text-[10px] text-white/40">
          {t("estimate.disclaimer", "Distances and durations are computed via OSRM road routing. Actual charge may vary based on tolls, waiting time and traffic.")}
        </div>
      </div>
    </div>
  );
}

function RateChip({ label, value }) {
  return (
    <div className="rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-[11px]">
      <span className="text-white/50 font-bold">{label}: </span>
      <span className="text-white font-black">{value}</span>
    </div>
  );
}

function PlaceInput({ placeholder, value, onChange, onUseMyLocation, testid }) {
  const { t } = useTranslation();
  const [text, setText] = useState(value?.label || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounce = useRef(null);

  useEffect(() => { setText(value?.label || ""); }, [value]);

  const onType = (v) => {
    setText(v);
    if (debounce.current) clearTimeout(debounce.current);
    if (!v || v.length < 3) { setResults([]); setOpen(false); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      const r = await searchPlaces(v);
      setResults(r);
      setOpen(true);
      setSearching(false);
    }, 350);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <MapPin size={14} className="text-white/40 shrink-0" />
        <input
          value={text}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-electric-teal"
          data-testid={`${testid}-input`}
        />
        {onUseMyLocation && (
          <button
            type="button"
            onClick={onUseMyLocation}
            className="shrink-0 text-[10px] font-black uppercase bg-white/10 text-white rounded-full px-2.5 py-2"
            data-testid={`${testid}-use-current`}
          >
            {t("estimate.myLocation", "Me")}
          </button>
        )}
      </div>
      {open && (searching || results.length > 0) && (
        <div className="absolute z-30 left-6 right-0 mt-1 rounded-xl bg-obsidian border border-white/10 shadow-lg max-h-60 overflow-y-auto">
          {searching ? (
            <div className="p-2 text-xs text-white/50 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> …</div>
          ) : results.map((r, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange({ label: r.label, lat: r.lat, lng: r.lng }); setText(r.label); setOpen(false); }}
              className="block w-full text-start text-xs text-white/80 px-3 py-2 hover:bg-white/5"
              data-testid={`${testid}-result-${i}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
